import { CloudUpload, Download, Upload, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  BrowseButton,
  ClearButton,
  Container,
  DownloadLink,
  HelperList,
  HelperListItem,
  HiddenInput,
  SelectedFile,
  UploadBox,
  UploadContent,
  UploadDescription,
  UploadIcon,
  UploadTitle,
} from './ExcelUploader.styles'
import { Button, Flex } from '@/components'

interface ExcelUploadProps {
  file?: File | null
  onFileSelect: (file: File | null) => void
  onImport: () => void
  isImporting?: boolean
  acceptedFormats?: string[]
}

export const ExcelUpload: React.FC<ExcelUploadProps> = ({
  file: externalFile,
  onFileSelect,
  onImport,
  isImporting = false,
  acceptedFormats = ['.xls', '.xlsx'],
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  useEffect(() => {
    if (externalFile === null) {
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [externalFile])

  function selectFile(file: File) {
    setSelectedFile(file)
    onFileSelect(file)
  }

  function clearFile(event: React.MouseEvent) {
    event.stopPropagation()
    setSelectedFile(null)
    onFileSelect(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) selectFile(file)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files[0]
    if (file && acceptedFormats.some((format) => file.name.endsWith(format))) {
      selectFile(file)
    }
  }

  return (
    <Container>
      <UploadBox onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} $isDragging={isDragging}>
        <UploadIcon>
          <CloudUpload />
        </UploadIcon>
        <UploadContent>
          <UploadTitle>Importação rápida via Excel</UploadTitle>
          <UploadDescription>
            Arraste e solte a sua planilha ({acceptedFormats.join(', ')}) aqui ou clique para selecionar.{' '}
            <DownloadLink href="/templates/template-canais.xlsx" download="template-canais.xlsx">
              <Download size={14} />
              Baixar template
            </DownloadLink>
          </UploadDescription>

          <HelperList>
            <HelperListItem>
              <strong>Nome do veículo</strong> — nome do canal (se já existir na empresa, a linha atualiza ele em vez de
              duplicar)
            </HelperListItem>
            <HelperListItem>
              <strong>Tipo de Mídia</strong> — obrigatório, preencha <strong>Radio</strong>, <strong>Online</strong>,{' '}
              <strong>Televisão</strong> ou <strong>Impresso</strong>
            </HelperListItem>
            <HelperListItem>
              <strong>TIER</strong> — SUPER TIER, 1, 2, 3, 4 ou 5 (use EXCLUIR pra parar de coletar o canal: ele fica
              cadastrado com <strong>Ignorar Canal</strong> ligado)
            </HelperListItem>
            <HelperListItem>
              <strong>Fonte de Dados</strong> — obrigatório, preencha <strong>Redes</strong> ou{' '}
              <strong>Imprensa</strong>
            </HelperListItem>
            <HelperListItem>
              <strong>Peso</strong> — número usado no cálculo de relevância só pra canais de <strong>Redes</strong>; em{' '}
              <strong>Imprensa</strong> o peso considerado é o do Tier, na configuração geral do Cena
            </HelperListItem>
            <HelperListItem>
              <strong>Link do Canal</strong> — opcional
            </HelperListItem>
            <HelperListItem>
              <strong>Preço Base</strong> — opcional, só números
            </HelperListItem>
            <HelperListItem>
              <strong>País</strong>, <strong>Região</strong> e <strong>Estado</strong> — opcionais (Estado em UF, ex:
              SP)
            </HelperListItem>
            <HelperListItem>
              <strong>Ignorar Verificação</strong> — opcional, preencha <strong>SIM</strong> pra o canal entrar sem
              verificar conteúdo com os termos das marcas
            </HelperListItem>
            <HelperListItem>
              <strong>Ignorar Canal</strong> — opcional, preencha <strong>SIM</strong> pra as postagens desse canal não
              entrarem no sistema
            </HelperListItem>
          </HelperList>
          {selectedFile && (
            <SelectedFile>
              <Flex $type="row" $align="center" $gap={8}>
                Arquivo selecionado: <strong>{selectedFile.name}</strong>
                <ClearButton type="button" onClick={clearFile} title="Remover arquivo">
                  <X size={14} />
                </ClearButton>
              </Flex>
            </SelectedFile>
          )}
        </UploadContent>
        <Flex $type="column" $align="center" $gap={8}>
          <BrowseButton onClick={() => fileInputRef.current?.click()}>Procurar Ficheiro</BrowseButton>
          <HiddenInput ref={fileInputRef} type="file" accept={acceptedFormats.join(',')} onChange={handleFileChange} />
        </Flex>
      </UploadBox>

      {selectedFile && (
        <Button.Root
          type="button"
          $variant="primary"
          onClick={onImport}
          disabled={isImporting}
          style={{ width: '100%', marginTop: '12px' }}>
          <Button.Icon value={Upload} size={16} />
          <Button.Text value={isImporting ? 'Importando...' : 'Importar Planilha'} />
        </Button.Root>
      )}
    </Container>
  )
}
